import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import {
  Button,
  CardMedia,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import Encoding from 'encoding-japanese';
import { doc, getDoc } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';

import { getMediaImage } from '../../api/aws/s3';
import { mediasRef } from '../../api/firebase/firestore/media';
import noImage from '../../components/assets/noImage.svg';
import {
  black,
  primarycolor,
  secondaryButtonnBackground,
  tertiaryButtonBackground
} from '../../constants/Colors';
import { mypageHeader, mypageHeaderTitle } from '../../constants/Location';
import {
  categoryList,
  funnelList,
  mediaBudgetList,
  mediaTargetList,
  mediaTypeList,
  useIndustry
} from '../../constants/Select';
import { screenType } from '../../constants/enum';

import type { MediaType } from '../../api/firebase/firestore/media/mediaType';
import type { SelectOption } from '../../constants/Select';

const styles = {
  aside: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    position: 'static',
    width: '100%'
  },
  budgetColumn: {
    width: '120px'
  },
  button: {
    color: primarycolor,
    height: '36px',
    mt: '16px',
    width: '100%'
  },
  buttonText: {
    color: primarycolor,
    fontSize: '14px'
  },
  csvButton: {
    color: black
  },
  demographicColumn: {
    width: '272px'
  },
  funnelColumn: {
    width: '120px'
  },
  genreColumn: {
    width: '120px'
  },
  imageColumn: {
    width: '184px'
  },
  mediaTypeColumn: {
    width: '120px'
  },
  mypageHeader,
  mypageHeaderTitle,
  nameColumn: {
    fontWeight: 700,
    width: '172px'
  },
  scaleColumn: {
    width: '140px'
  },
  secondaryButtonnBackground,
  table: {
    bgcolor: 'white',
    tableLayout: 'fixed'
  },
  tableCell: {
    border: '1px solid #DBDBE0',
    boxSizing: 'border-box',
    color: black,
    fontFamily: 'Roboto',
    fontSize: '14px',
    overflowWrap: 'anywhere',
    verticalAlign: 'top',
    whiteSpace: 'pre-wrap'
  },
  tableHeader: {
    bgcolor: '#DBDBE0'
  },
  tableHeaderCell: {
    borderRight: '1px solid #B3B3B3',
    boxSizing: 'border-box',
    color: black,
    fontFamily: 'Roboto',
    fontSize: '14px',
    fontWeight: 700
  },
  tableWrapper: {
    overflowX: 'auto'
  },
  targetColumn: {
    width: '120px'
  },
  tertiaryButtonBackground
};

interface Data {
  id: string;
  image: string;
  name: string;
  genre: string;
  mediaType: string;
  funnel: string;
  target: string;
  scale: string;
  gender: {
    male: number;
    female: number;
  };
  gene: {
    f10: number;
    f20: number;
    f30: number;
    f40: number;
    f50: number;
    f60: number;
    f70: number;
    m10: number;
    m20: number;
    m30: number;
    m40: number;
    m50: number;
    m60: number;
    m70: number;
  };
  area: {
    hokkaidou: number;
    touhoku: number;
    kantou: number;
    chubu: number;
    kinki: number;
    chugoku: number;
    shikoku: number;
    kyushu: number;
  };
  budget: string;
}

interface Props {
  medias: string[];
  screen: number;
}

const MypageMediaCompare: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { medias, screen } = props;
  const [mediaData, setMediaData] = useState<Data[]>([]);
  const cList = categoryList();
  const mTypeList = mediaTypeList();
  const tList = mediaTargetList();
  const fList = funnelList();
  const bList = mediaBudgetList();
  const iList = useIndustry();
  const csvHeader = [
    t('media.name'),
    t('media.overview'),
    t('media.genre'),
    t('media.mediaType'),
    t('media.funnel'),
    t('media.target'),
    t('media.scaleDetail.reach'),
    t('media.scaleDetail.scaleUu'),
    t('media.gender'),
    t('media.generation'),
    t('media.accessArea'),
    t('media.budget'),
    t('media.industries'),
    t('media.continuousFactors')
  ];

  const [csvData, setCsvData] = useState<string[][]>([csvHeader]);

  const showValueToName = (
    selectOption: SelectOption[],
    itemValue: number | undefined
  ): string => {
    const d = selectOption.find((element) => element.value === itemValue);
    return d?.option ?? '';
  };

  const showMediaType = (v: number): string => {
    const datas = mTypeList.map((item) =>
      item.options.find((i) => i.value === v)
    );
    const d = datas.filter((item) => item !== undefined);
    return d[0]?.option ?? '';
  };

  useEffect(() => {
    void (() => {
      if (medias) {
        medias.map(async (mediaId) => {
          const mediaRef = doc(mediasRef, mediaId);
          const mediaDoc = await getDoc(mediaRef);
          const data = mediaDoc.data() as MediaType;
          if (data) {
            const image = await getMediaImage(data.image);
            const categoryArray = data.category?.map((item: number) =>
              showValueToName(cList, item)
            );
            const mediaType = showMediaType(data.mediaType);
            const funnelArray = data.funnel?.map((item: number) =>
              showValueToName(fList, item)
            );
            const targetArray = data.target?.map((item: number) =>
              showValueToName(tList, item)
            );
            // 月間PV数のタイトルをメディア種別によって出し分け
            const pvTextSwitcher = (type: string): string => {
              let mediaTypeName;
              for (const group of mTypeList) {
                for (const option of group.options) {
                  if (option.option === type) {
                    mediaTypeName = group.name;
                  }
                }
              }
              if (
                mediaTypeName === t('media.mediaTypeOption.tv') ||
                mediaTypeName === t('media.mediaTypeOption.radio')
              ) {
                return t('media.scaleDetail.sacleNet');
              } else if (
                mediaTypeName === t('media.mediaTypeOption.paper') ||
                mediaTypeName === t('media.mediaTypeOption.magazine')
              ) {
                return t('media.scaleDetail.scaleCirculation');
              } else if (mediaTypeName === t('media.mediaTypeOption.web')) {
                return t('media.scaleDetail.scalePvMau');
              }
              return t('media.scaleDetail.scaleOoh');
            };

            // 月間UU数のタイトルをメディア種別によって出し分け
            const uuTextSwitcher = (type: string): string => {
              let mediaTypeName;
              for (const group of mTypeList) {
                for (const option of group.options) {
                  if (option.option === type) {
                    mediaTypeName = group.name;
                  }
                }
              }
              if (
                mediaTypeName === t('media.mediaTypeOption.tv') ||
                mediaTypeName === t('media.mediaTypeOption.radio')
              ) {
                return '';
              } else if (
                mediaTypeName === t('media.mediaTypeOption.paper') ||
                mediaTypeName === t('media.mediaTypeOption.magazine')
              ) {
                return t('media.scaleDetail.scalReaders');
              } else if (mediaTypeName === t('media.mediaTypeOption.web')) {
                return t('media.scaleDetail.scaleUu');
              }
              return '';
            };

            const showScaleData = [
              `${pvTextSwitcher(mediaType)} : ${data.scalePvMau}`,
              ...(uuTextSwitcher(mediaType) !== ''
                ? [`${uuTextSwitcher(mediaType)} : ${data.scaleUu ?? ''}`]
                : []),
              `${t('media.scaleDetail.scaleEmailSubscribers')} : ${
                data.scaleMembers ?? ''
              }`
            ];
            const scaleText = showScaleData.join('\n');
            const budget = showValueToName(bList, data.budget);
            const list = {
              area: {
                chubu: data.demographicsArea?.chubu,
                chugoku: data.demographicsArea?.chugoku,
                hokkaidou: data.demographicsArea?.hokkaidou,
                kantou: data.demographicsArea?.kantou,
                kinki: data.demographicsArea?.kinki,
                kyushu: data.demographicsArea?.kyushu,
                shikoku: data.demographicsArea?.shikoku,
                touhoku: data.demographicsArea?.touhoku
              },
              budget,
              funnel: funnelArray?.join('、'),
              gender: {
                female: data.demographicsGender?.female,
                male: data.demographicsGender?.male
              },
              gene: {
                f10: data.demographicsGeneration?.f10,
                f20: data.demographicsGeneration?.f20,
                f30: data.demographicsGeneration?.f30,
                f40: data.demographicsGeneration?.f40,
                f50: data.demographicsGeneration?.f50,
                f60: data.demographicsGeneration?.f60,
                f70: data.demographicsGeneration?.f70,
                m10: data.demographicsGeneration?.m10,
                m20: data.demographicsGeneration?.m20,
                m30: data.demographicsGeneration?.m30,
                m40: data.demographicsGeneration?.m40,
                m50: data.demographicsGeneration?.m50,
                m60: data.demographicsGeneration?.m60,
                m70: data.demographicsGeneration?.m70
              },
              genre: categoryArray?.join('、'),
              id: mediaId,
              image: image ?? noImage,
              mediaType,
              name: data.name,
              scale: scaleText,
              target: targetArray?.join('、')
            };
            const dataForCsv = [
              data.name,
              data.overview,
              categoryArray?.join('、'),
              mediaType,
              funnelArray?.join('、'),
              targetArray?.join('、'),
              String(data.scalePvMau),
              String(data.scaleUu),
              `${t('media.genderOption.female')}:${
                data.demographicsGender?.female
              }%、${t('media.genderOption.male')}:${
                data.demographicsGender?.male
              }%`,
              `${t('media.generationOption.10')}:${
                data.demographicsGeneration?.f10 +
                data.demographicsGeneration?.m10
              }%、${t('media.generationOption.20')}:${
                data.demographicsGeneration?.f20 +
                data.demographicsGeneration?.m20
              }%、${t('media.generationOption.30')}:${
                data.demographicsGeneration?.f30 +
                data.demographicsGeneration?.m30
              }%、${t('media.generationOption.40')}:${
                data.demographicsGeneration?.f40 +
                data.demographicsGeneration?.m40
              }%、${t('media.generationOption.50')}:${
                data.demographicsGeneration?.f50 +
                data.demographicsGeneration?.m50
              }%、${t('media.generationOption.60')}:${
                data.demographicsGeneration?.f60 +
                data.demographicsGeneration?.m60
              }%、${t('media.generationOption.70')}:${
                data.demographicsGeneration?.f70 +
                data.demographicsGeneration?.m70
              }%`,
              `${t('media.accessAreaOption.hokkaido')}:${
                data.demographicsArea?.hokkaidou
              }%、${t('media.accessAreaOption.tohoku')}:${
                data.demographicsArea?.touhoku
              }%、${t('media.accessAreaOption.kanto')}:${
                data.demographicsArea?.kantou
              }%、${t('media.accessAreaOption.chubu')}:${
                data.demographicsArea?.chubu
              }%、${t('media.accessAreaOption.kinki')}:${
                data.demographicsArea?.kinki
              }%、${t('media.accessAreaOption.chugoku')}:${
                data.demographicsArea?.chugoku
              }%、${t('media.accessAreaOption.shikoku')}:${
                data.demographicsArea?.shikoku
              }%、${t('media.accessAreaOption.kyushu')}:${
                data.demographicsArea?.kyushu
              }%`,
              budget.replace(',', '').replace('¥', '￥').replace('〜', '~'),
              `１：${showValueToName(
                iList,
                data.industries?.[0].industriesNum
              )}${
                data.industries?.[0].percentage
                  ? `:${data.industries?.[0].percentage}%`
                  : ''
              }、２：${showValueToName(
                iList,
                data.industries?.[1].industriesNum
              )}${
                data.industries?.[1].percentage
                  ? `:${data.industries?.[1].percentage}%`
                  : ''
              }、３：${showValueToName(
                iList,
                data.industries?.[2].industriesNum
              )}${
                data.industries?.[2].percentage
                  ? `:${data.industries?.[2].percentage}%`
                  : ''
              }`,
              `１:${data.continuousFactors?.[0]}、２：${data.continuousFactors?.[1]}、３：${data.continuousFactors?.[2]}`
            ];
            const processingData = dataForCsv.map((row) =>
              String(row).replace(/"/g, '""').replace(/\n/g, ' ')
            );
            setMediaData((prev) => [...prev, list]);
            setCsvData((prev) => [...prev, processingData]);
          }
        });
      }
    })();
  }, [medias]);

  const handleDownload = (): void => {
    const newCsvData = csvData?.map((row) => row.join(',')).join('\n');
    const unicodeArray = Encoding.stringToCode(newCsvData);
    const sjisArray = Encoding.convert(unicodeArray, {
      from: 'UNICODE',
      to: 'SJIS'
    });
    const blob = new Blob([new Uint8Array(sjisArray)], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `mediaCompare_${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Typography component="div" sx={styles.aside}>
      <Typography component="div" sx={{ width: '100%' }}>
        <Typography sx={styles.mypageHeader} component="div">
          <Typography sx={styles.mypageHeaderTitle}>
            {t('media.mediaCompare')}
          </Typography>
          <Button sx={styles.tertiaryButtonBackground} onClick={handleDownload}>
            <FileDownloadOutlinedIcon sx={styles.csvButton} />
            <Typography sx={styles.csvButton}>
              {t('media.csvDownload')}
            </Typography>
          </Button>
        </Typography>
        <Grid sx={{ margin: '24px' }}>
          <TableContainer sx={styles.tableWrapper}>
            <Table sx={styles.table}>
              <TableHead>
                <TableRow sx={styles.tableHeader}>
                  <TableCell
                    sx={[styles.tableHeaderCell, styles.imageColumn]}
                  ></TableCell>
                  <TableCell sx={[styles.tableHeaderCell, styles.nameColumn]}>
                    {t('media.name')}
                  </TableCell>
                  <TableCell sx={[styles.tableHeaderCell, styles.genreColumn]}>
                    {t('media.genre')}
                  </TableCell>
                  <TableCell
                    sx={[styles.tableHeaderCell, styles.mediaTypeColumn]}
                  >
                    {t('media.mediaType')}
                  </TableCell>
                  <TableCell sx={[styles.tableHeaderCell, styles.funnelColumn]}>
                    {t('media.funnel')}
                  </TableCell>
                  <TableCell sx={[styles.tableHeaderCell, styles.targetColumn]}>
                    {t('media.target')}
                  </TableCell>
                  <TableCell sx={[styles.tableHeaderCell, styles.scaleColumn]}>
                    {t('media.scale')}
                  </TableCell>
                  <TableCell
                    sx={[styles.tableHeaderCell, styles.demographicColumn]}
                  >
                    {t('media.demographic')}
                  </TableCell>
                  <TableCell
                    sx={[
                      styles.tableHeaderCell,
                      styles.budgetColumn,
                      { borderRight: 'none' }
                    ]}
                  >
                    {t('media.budget')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {mediaData.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell sx={[styles.tableCell, styles.imageColumn]}>
                      <CardMedia component="img" image={row.image} />
                      <Button
                        sx={[styles.secondaryButtonnBackground, styles.button]}
                        onClick={() => {
                          if (screen === screenType.client) {
                            navigate(`/client/mypage/media/${row.id}`);
                          } else if (screen === screenType.agent) {
                            navigate(`/agent/mypage/media/${row.id}`);
                          }
                        }}
                      >
                        <Typography sx={styles.buttonText}>
                          {t('media.toMediaDetail')}
                        </Typography>
                      </Button>
                    </TableCell>
                    <TableCell sx={[styles.tableCell, styles.nameColumn]}>
                      {row.name}
                    </TableCell>
                    <TableCell sx={[styles.tableCell, styles.genreColumn]}>
                      {row.genre}
                    </TableCell>
                    <TableCell sx={[styles.tableCell, styles.mediaTypeColumn]}>
                      {row.mediaType}
                    </TableCell>
                    <TableCell sx={[styles.tableCell, styles.funnelColumn]}>
                      {row.funnel}
                    </TableCell>
                    <TableCell sx={[styles.tableCell, styles.targetColumn]}>
                      {row.target}
                    </TableCell>
                    <TableCell sx={[styles.tableCell, styles.scaleColumn]}>
                      {row.scale}
                    </TableCell>
                    <TableCell
                      sx={[styles.tableCell, styles.demographicColumn]}
                    >
                      <Grid>
                        <Typography
                          sx={{
                            color: black,
                            fontFamily: 'Roboto',
                            fontSize: '14px',
                            fontWeight: 700
                          }}
                        >
                          {t('media.gender')}
                        </Typography>
                        <Typography
                          sx={{
                            color: black,
                            fontFamily: 'Roboto',
                            fontSize: '14px'
                          }}
                        >
                          {t('media.genderOption.male')} : {row.gender.male}% ,{' '}
                          {t('media.genderOption.female')} : {row.gender.female}
                          %
                        </Typography>
                        <Typography
                          sx={{
                            color: black,
                            fontFamily: 'Roboto',
                            fontSize: '14px',
                            fontWeight: 700
                          }}
                        >
                          {t('media.generation')}
                        </Typography>
                        <Typography
                          sx={{
                            color: black,
                            fontFamily: 'Roboto',
                            fontSize: '14px'
                          }}
                        >
                          {t('media.generationOption.10')} :{' '}
                          {row.gene.f10 + row.gene.m10}% ,{' '}
                          {t('media.generationOption.20')} :{' '}
                          {row.gene.f20 + row.gene.m20}% ,{' '}
                          {t('media.generationOption.30')} :{' '}
                          {row.gene.f30 + row.gene.m30}% ,{' '}
                          {t('media.generationOption.40')} :{' '}
                          {row.gene.f40 + row.gene.m40}% ,{' '}
                          {t('media.generationOption.50')} :{' '}
                          {row.gene.f50 + row.gene.m50}% ,{' '}
                          {t('media.generationOption.60')} :{' '}
                          {row.gene.f60 + row.gene.m60}% ,{' '}
                          {t('media.generationOption.70')} :{' '}
                          {row.gene.f70 + row.gene.m70}%
                        </Typography>
                        <Typography
                          sx={{
                            color: black,
                            fontFamily: 'Roboto',
                            fontSize: '14px',
                            fontWeight: 700
                          }}
                        >
                          {t('media.accessArea')}
                        </Typography>
                        <Typography
                          sx={{
                            color: black,
                            fontFamily: 'Roboto',
                            fontSize: '14px'
                          }}
                        >
                          {t('media.accessAreaOption.hokkaido')} :{' '}
                          {row.area.hokkaidou}% ,{' '}
                          {t('media.accessAreaOption.tohoku')} :{' '}
                          {row.area.touhoku}% ,{' '}
                          {t('media.accessAreaOption.kanto')} :{' '}
                          {row.area.kantou}% ,{' '}
                          {t('media.accessAreaOption.chubu')} : {row.area.chubu}
                          % , {t('media.accessAreaOption.kinki')} :{' '}
                          {row.area.kinki}% ,{' '}
                          {t('media.accessAreaOption.chugoku')} :{' '}
                          {row.area.chugoku}% ,{' '}
                          {t('media.accessAreaOption.shikoku')} :{' '}
                          {row.area.shikoku}% ,{' '}
                          {t('media.accessAreaOption.kyushu')} :{' '}
                          {row.area.kyushu}%
                        </Typography>
                      </Grid>
                      {/* {row.demographic} */}
                    </TableCell>
                    <TableCell sx={[styles.tableCell, styles.budgetColumn]}>
                      {row.budget}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Typography>
    </Typography>
  );
};

export default MypageMediaCompare;
